/**
 * useAdminRefunds Hook
 *
 * Lists refundRequests for the admin dashboard, enriched with guest info.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export type RefundStatus = 'pending' | 'processed' | 'rejected';

export interface AdminRefund {
  id: string;
  bookingId: string;
  listingId: string;
  listingTitle: string;
  guestId: string;
  guestName: string;
  guestEmail: string;
  hostId: string;
  amountPaid: number;
  suggestedRefund: number;
  refundPercent: number;
  refundReason?: string;
  policy: string;
  cancelledBy: 'guest' | 'host';
  paymentMethod: string;
  status: RefundStatus;
  approvedAmount?: number;
  rejectionReason?: string;
  stripeRefundId?: string;
  manualRefundReference?: string;
  createdAt: Date;
  processedAt?: Date;
}

export interface RefundsStats {
  pending: number;
  processed: number;
  rejected: number;
  pendingAmount: number;
}

const toDate = (val: unknown): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val as string) : new Date();
};

export const useAdminRefunds = (statusFilter: RefundStatus) => {
  const [refunds, setRefunds] = useState<AdminRefund[]>([]);
  const [stats, setStats] = useState<RefundsStats>({
    pending: 0,
    processed: 0,
    rejected: 0,
    pendingAmount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const fetchRefunds = async () => {
      setIsLoading(true);
      try {
        const [refundsSnap, usersSnap] = await Promise.all([
          getDocs(query(collection(db, 'refundRequests'), orderBy('createdAt', 'desc'))),
          getDocs(collection(db, 'users')),
        ]);

        const userMap = new Map<string, { name: string; email: string }>();
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          userMap.set(d.id, {
            name:
              data.displayName ||
              `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
              'Unknown',
            email: data.email || '',
          });
        });

        const all: AdminRefund[] = refundsSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const guest = userMap.get(data.guestId) || { name: 'Unknown', email: '' };
          return {
            id: docSnap.id,
            bookingId: data.bookingId || '',
            listingId: data.listingId || '',
            listingTitle: data.listingTitle || '',
            guestId: data.guestId || '',
            guestName: guest.name,
            guestEmail: guest.email,
            hostId: data.hostId || '',
            amountPaid: data.amountPaid || 0,
            suggestedRefund: data.suggestedRefund || 0,
            refundPercent: data.refundPercent || 0,
            refundReason: data.refundReason,
            policy: data.policy || 'flexible',
            cancelledBy: data.cancelledBy === 'host' ? 'host' : 'guest',
            paymentMethod: data.paymentMethod || 'stripe',
            status: (['pending', 'processed', 'rejected'].includes(data.status)
              ? data.status
              : 'pending') as RefundStatus,
            approvedAmount: data.approvedAmount,
            rejectionReason: data.rejectionReason,
            stripeRefundId: data.stripeRefundId,
            manualRefundReference: data.manualRefundReference,
            createdAt: toDate(data.createdAt),
            processedAt: data.processedAt ? toDate(data.processedAt) : undefined,
          };
        });

        const computedStats: RefundsStats = {
          pending: all.filter((r) => r.status === 'pending').length,
          processed: all.filter((r) => r.status === 'processed').length,
          rejected: all.filter((r) => r.status === 'rejected').length,
          pendingAmount: all
            .filter((r) => r.status === 'pending')
            .reduce((sum, r) => sum + r.suggestedRefund, 0),
        };

        if (!cancelled) {
          setStats(computedStats);
          setRefunds(all.filter((r) => r.status === statusFilter));
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch refund requests:', err);
        if (!cancelled) {
          setRefunds([]);
          setIsLoading(false);
        }
      }
    };

    fetchRefunds();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, fetchKey]);

  return { refunds, stats, isLoading, refetch };
};

export default useAdminRefunds;
